<script lang="ts">
    import type * as Phaser from "phaser";
    import { onDestroy } from "svelte";
    import { LL } from "../../../../i18n/i18n-svelte";
    import { gameManager } from "../../../Phaser/Game/GameManager";
    import {
        mapEditorSelectedTileIndexStore,
        mapEditorSelectedTilesetIndexStore,
    } from "../../../Stores/MapEditorStore";
    const gameMapFrontWrapper = gameManager.getCurrentGameScene().getGameMapFrontWrapper();
    const tilesets = gameMapFrontWrapper.phaserMap.tilesets;

    let selectedTilesetIndex = $state(0);
    let selectedTileGid = $state<number | undefined>(undefined);

    const selectedTileset = $derived(tilesets[selectedTilesetIndex]);
    const selectedTilesetImage = $derived(selectedTileset ? getTilesetImage(selectedTileset) : undefined);

    const tilesetIndexUnsubscriber = mapEditorSelectedTilesetIndexStore.subscribe((index) => {
        selectedTilesetIndex = clampTilesetIndex(index);
    });

    const selectedTileGidUnsubscriber = mapEditorSelectedTileIndexStore.subscribe((gid) => {
        selectedTileGid = gid;
    });

    onDestroy(() => {
        tilesetIndexUnsubscriber();
        selectedTileGidUnsubscriber();
    });

    function clampTilesetIndex(index: number): number {
        return Math.max(0, Math.min(index, tilesets.length - 1));
    }

    function selectTileset(index: number) {
        selectedTilesetIndex = index;
        mapEditorSelectedTilesetIndexStore.set(index);
    }

    function selectTile(tileset: Phaser.Tilemaps.Tileset, tileIndex: number) {
        mapEditorSelectedTileIndexStore.set(tileset.firstgid + tileIndex);
    }

    function getTilesetImage(
        tileset: Phaser.Tilemaps.Tileset,
    ): { src: string; width: number; height: number } | undefined {
        const texture = tileset.image;
        if (!texture) {
            return undefined;
        }
        const source = texture.getSourceImage();
        if (source instanceof HTMLCanvasElement) {
            return { src: source.toDataURL(), width: source.width, height: source.height };
        }
        if (source instanceof HTMLImageElement) {
            return {
                src: source.src,
                width: source.naturalWidth || source.width,
                height: source.naturalHeight || source.height,
            };
        }
        return undefined;
    }

    function getTileTexturePosition(tileset: Phaser.Tilemaps.Tileset, index: number): { x: number; y: number } {
        const tileX = index % tileset.columns;
        const tileY = Math.floor(index / tileset.columns);
        return {
            x: tileset.tileMargin + tileX * (tileset.tileWidth + tileset.tileSpacing),
            y: tileset.tileMargin + tileY * (tileset.tileHeight + tileset.tileSpacing),
        };
    }

    function getTileStyle(
        tileset: Phaser.Tilemaps.Tileset,
        index: number,
        image: { src: string; width: number; height: number },
    ): string {
        const { x, y } = getTileTexturePosition(tileset, index);
        return `width: ${tileset.tileWidth}px; height: ${tileset.tileHeight}px; background-image: url("${image.src}"); background-position: -${x}px -${y}px; background-size: ${image.width}px ${image.height}px;`;
    }
</script>

{#if tilesets.length > 0}
    <div class="flex flex-col gap-2">
        <h4 class="text-sm font-semibold">{$LL.mapEditor.tilemapEditor.tiles()}</h4>
        <div class="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
            {#each tilesets as tileset, index (tileset.name)}
                <button
                    type="button"
                    class="px-2 py-1 text-xs rounded {index === selectedTilesetIndex
                        ? 'bg-secondary'
                        : 'bg-white/10 hover:bg-white/20'}"
                    onclick={() => selectTileset(index)}
                >
                    {tileset.name}
                </button>
            {/each}
        </div>
        {#if selectedTileset && selectedTilesetImage}
            <div
                class="tile-grid max-h-72 overflow-y-auto"
                style="grid-template-columns: repeat({selectedTileset.columns}, {selectedTileset.tileWidth}px);"
            >
                {#each Array.from({ length: selectedTileset.total }) as _, tileIndex (tileIndex)}
                    {@const gid = selectedTileset.firstgid + tileIndex}
                    <button
                        type="button"
                        class="tile"
                        class:selected={selectedTileGid === gid}
                        style={getTileStyle(selectedTileset, tileIndex, selectedTilesetImage)}
                        aria-label={`${selectedTileset.name} ${tileIndex + 1}`}
                        onclick={() => selectTile(selectedTileset, tileIndex)}
                    ></button>
                {/each}
            </div>
        {/if}
    </div>
{:else}
    <p class="text-sm text-white/70">{$LL.mapEditor.tilemapEditor.noTilesetFound()}</p>
{/if}

<style>
    .tile-grid {
        display: grid;
        width: max-content;
        gap: 1px;
    }

    .tile {
        padding: 0;
        border: 1px solid transparent;
        border-radius: 2px;
        background-repeat: no-repeat;
        image-rendering: pixelated;
        cursor: pointer;
    }

    .tile:hover {
        border-color: rgba(255, 255, 255, 0.6);
    }

    .tile.selected {
        border-color: #fff;
        box-shadow: 0 0 0 2px #000 inset;
    }
</style>
